
import { CrudModel } from "~/plugins/Cruxtify/CrudModel";
import { RESULT_TYPES, getResultsByType } from "../Results.vue";

const PRICED_COMPONENT_CLASS = "App\\Models\\PricedComponent";
const DYNAMIC_ADDER_CLASS = "App\\Models\\DynamicAdder";

export default {
  data() {
    return {
      key: 0,
    };
  },
  props: {
    singleJsonResult: Object,
  },
  methods: {
    // Copied from Tables/Prices.vue:42-48 — formats a numeric contribution
    // using the result's currency symbol.
    decoratePrice(price) {
      if (price === null || typeof price === "undefined") return "";

      return (
        this.singleJsonResult.currency.symbol +
        Number(price).toFixed(2).toLocaleString()
      );
    },
    // Builds a { link, label } pair for a PricedComponent the same way
    // LogicData.vue:78-96 builds ModelLink props. Memoized per derivation so a
    // component shared by its direct-price row and its adder rows is built once.
    buildComponentLink(componentObj) {
      const data = componentObj._data;
      if (!data || !data.id) return null;

      if (!this._componentLinkCache) this._componentLinkCache = {};
      if (this._componentLinkCache[data.id]) {
        return this._componentLinkCache[data.id];
      }

      const modelClass = CrudModel.getModelType("PricedComponent");
      const modelInstance = new modelClass(data);
      const link = {
        link: modelInstance.getRouteSingle(),
        label: modelInstance.label,
      };

      this._componentLinkCache[data.id] = link;
      return link;
    },
    // The Type column shows what the row's price is: a direct component
    // price, or a capitalized adder type suffixed with " Adder"
    // (e.g. "setup" → "Setup Adder").
    typeLabel(row) {
      if (row.kind === "component-direct") return "Direct";
      if (row.kind === "dynamic_adder") return "Dynamic Adder";

      const type = row.adderType;
      if (!type) return "Adder";

      return type.charAt(0).toUpperCase() + type.slice(1) + " Adder";
    },
  },
  computed: {
    // { part_number -> step label } map logged by PricingSchema. The frontend
    // joins on it: a component's part_number equals the mapping's resolved
    // string, so this attributes each row to the mapping step it came from.
    stepLabels() {
      const results = getResultsByType(
        this.singleJsonResult,
        RESULT_TYPES.COMPONENT_STEP_LABELS
      );
      // Logged at most once; its result is the { string -> label } object.
      return results.length ? results[results.length - 1] || {} : {};
    },
    // Component-direct rows: one per PRICED_COMPONENTS [qtyToken, component]
    // pair. A component with no direct-price entry produces no such row.
    componentRows() {
      const results = getResultsByType(
        this.singleJsonResult,
        RESULT_TYPES.PRICED_COMPONENTS
      );
      // PRICED_COMPONENTS is logged at most once; its result is the list of pairs.
      const pairs = results.length ? results[results.length - 1] : [];

      return pairs.map(([qtyToken, componentObj]) => {
        const component = this.buildComponentLink(componentObj);

        return {
          kind: "component-direct",
          label: componentObj._data.part_number || componentObj._data.id,
          component,
          price: componentObj._data.price,
          adderType: null,
          source: `${qtyToken} · Priced Component`,
          step: this.stepLabels[componentObj._data.part_number] || null,
          originData: componentObj,
        };
      });
    },
    // Parsed `use_adder` entries (shared by static and dynamic adder rows).
    // Mirrors Tab/Adders.vue: result[0] = adder, result[1] = context,
    // result[2] = contributed price.
    useAdderEntries() {
      return getResultsByType(
        this.singleJsonResult,
        RESULT_TYPES.USE_ADDER
      ).map((result) => ({
        adderClass: result[0]._class,
        adderData: result[0]._data,
        context: result[1],
        price: result[2],
      }));
    },
    // Static adder rows: one flat row per adder (no summing, no type
    // collapsing). Parent component link is duplicated onto component-scoped
    // adder rows; global (string-context) adders get component: null.
    //
    // Dynamic ("Gold") adders are logged as DynamicAdder instances by
    // applyDynamicAdders() (PriceQuery.php:419-422); they are component-less,
    // so they're split out into `dynamicRows` and rendered in their own table.
    // (The _class check — not a dynamic_price check — isolates them: setup
    // adders (PriceQuery.php:614) and gold adders (GoldAdder.php:84) are plain
    // Adder instances that also carry a non-null dynamic_price.)
    adderRows() {
      return this.useAdderEntries
        .filter((adder) => adder.adderClass !== DYNAMIC_ADDER_CLASS)
        .map((adder) => {
          const context = adder.context;
          const isComponentContext =
            Array.isArray(context) &&
            context[0] &&
            context[0]._class === PRICED_COMPONENT_CLASS;

          let component = null;
          let source;
          // Adders inherit the step of their parent component (same join key);
          // global (string-context) adders have no component, hence no step.
          let step = null;

          if (isComponentContext) {
            const componentObj = context[0];
            component = this.buildComponentLink(componentObj);
            const partNumber = componentObj._data.part_number;
            step = this.stepLabels[partNumber] || null;
            source =
              adder.adderData.type === "setup"
                ? `Setup adder for ${partNumber}`
                : `Component adder for ${partNumber}`;
          } else {
            // Polymorphic context: string (e.g. global adders) → no parent.
            source = typeof context === "string" ? context : "Adder";
          }

          return {
            kind: "adder",
            label: adder.adderData.name,
            component,
            price: adder.price,
            adderType: adder.adderData.type,
            source,
            step,
            originData: context,
          };
        });
    },
    // Dynamic-adder rows. These don't hang off a price component — they carry
    // a string context (e.g. "Price Book's Gold Adders"), so component/step
    // are null and they're surfaced in their own table after the priced rows.
    dynamicRows() {
      return this.useAdderEntries
        .filter((adder) => adder.adderClass === DYNAMIC_ADDER_CLASS)
        .map((adder) => {
          const context = adder.context;

          return {
            kind: "dynamic_adder",
            label: adder.adderData.name,
            component: null,
            price: adder.price,
            adderType: adder.adderData.type,
            source: typeof context === "string" ? context : "Dynamic Adder",
            step: null,
            originData: context,
          };
        });
    },
    // Flat ordering (sort only, NOT nesting): each component's direct-price
    // row immediately followed by its own adder rows, then global adder rows.
    rows() {
      if (
        !this.singleJsonResult ||
        !Array.isArray(this.singleJsonResult.logic_monitor) ||
        this.singleJsonResult.logic_monitor.length === 0
      ) {
        return [];
      }

      const componentRows = this.componentRows;
      const linkOf = (row) => (row.component ? row.component.link : null);

      // Bucket adder rows by parent component link in a single pass; global
      // (component-less) adders go to their own list.
      const addersByLink = new Map();
      const globalAdders = [];
      this.adderRows.forEach((adderRow) => {
        const link = linkOf(adderRow);
        if (link === null) {
          globalAdders.push(adderRow);
          return;
        }
        if (!addersByLink.has(link)) addersByLink.set(link, []);
        addersByLink.get(link).push(adderRow);
      });

      const ordered = [];
      // Each component's direct-price row immediately followed by its adders.
      componentRows.forEach((componentRow) => {
        ordered.push(componentRow);
        const link = linkOf(componentRow);
        if (addersByLink.has(link)) {
          ordered.push(...addersByLink.get(link));
          addersByLink.delete(link);
        }
      });

      // Adders whose parent component had no direct-price row of its own.
      addersByLink.forEach((adderRows) => ordered.push(...adderRows));

      // Component-less (global) adder rows last.
      ordered.push(...globalAdders);

      return ordered;
    },
    // Zero-price contributions are pulled out of the main table and listed
    // separately below it; ordering within each group is preserved.
    nonZeroRows() {
      return this.rows.filter((row) => Number(row.price) !== 0);
    },
    zeroRows() {
      return this.rows.filter((row) => Number(row.price) === 0);
    },
  },
  watch: {
    singleJsonResult() {
      this.key++;
      this._componentLinkCache = {};
    },
  },
};
